import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Loader2, Plus, Trash2, Edit3, Save, X, Truck, ToggleLeft, ToggleRight,
  Star, Clock, ArrowUp, ArrowDown, RotateCcw, Sparkles, GripVertical, Package,
} from "lucide-react";

const EMPTY_COURIER = {
  courier_id: "", name: "", logo: "", tagline: "",
  slabs: [
    { min_kg: 0, max_kg: 0.5, price: 90 },
    { min_kg: 0.5, max_kg: 1.0, price: 120 },
    { min_kg: 1.0, max_kg: 3.0, price: 180 },
    { min_kg: 3.0, max_kg: 5.0, price: 260 },
    { min_kg: 5.0, max_kg: 10.0, price: 420 },
    { min_kg: 10.0, max_kg: 1000.0, price: 650 },
  ],
  per_km_extra: 0.04, days: 3, reliability: 4.0, active: true, sort_order: 99,
};

export default function AdminCouriers() {
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // full courier object being edited
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewParams, setPreviewParams] = useState({ distance_km: 10, weight_kg: 1 });

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/couriers/");
      setCouriers(data.couriers || []);
    } catch (err) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const toggleActive = async (c) => {
    try {
      await api.put(`/admin/couriers/${c.id}`, { ...c, active: !c.active });
      toast.success(`${c.name} ${!c.active ? "enabled" : "disabled"}`);
      fetch();
    } catch (err) { toast.error("Toggle failed"); }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= couriers.length) return;
    const reordered = [...couriers];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    setCouriers(reordered);
    try {
      await api.post("/admin/couriers/reorder", { ids_in_order: reordered.map((c) => c.id) });
    } catch (err) { toast.error("Reorder failed"); fetch(); }
  };

  const remove = async (c) => {
    if (!confirm(`Delete ${c.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/couriers/${c.id}`);
      toast.success("Deleted");
      fetch();
    } catch (err) { toast.error("Delete failed"); }
  };

  const save = async () => {
    if (!editing?.name?.trim() || !editing?.courier_id?.trim()) {
      toast.error("Name and ID are required"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        reliability: parseFloat(editing.reliability),
        days: parseInt(editing.days),
        per_km_extra: parseFloat(editing.per_km_extra || 0),
        sort_order: parseInt(editing.sort_order || 0),
        slabs: (editing.slabs || []).map((s) => ({
          min_kg: parseFloat(s.min_kg), max_kg: parseFloat(s.max_kg), price: parseFloat(s.price),
        })),
      };
      if (creating) {
        await api.post("/admin/couriers/", payload);
        toast.success("Courier created");
      } else {
        await api.put(`/admin/couriers/${editing.id}`, payload);
        toast.success("Saved");
      }
      setEditing(null);
      setCreating(false);
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const runPreview = async () => {
    try {
      const { data } = await api.post("/admin/couriers/preview", previewParams);
      setPreview(data);
    } catch (err) { toast.error("Preview failed"); }
  };

  const resetDefaults = async () => {
    if (!confirm("Reset all couriers to Playbook defaults? Your custom couriers will be deleted.")) return;
    try {
      await api.post("/admin/couriers/reset-defaults");
      toast.success("Reset complete");
      fetch();
    } catch (err) { toast.error("Reset failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-couriers">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Truck className="h-6 w-6 text-[#FF6B00]" /> Courier Rate Cards
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage every courier's weight-slab pricing, ETA & reliability. Changes reflect instantly.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetDefaults} className="rounded-lg" data-testid="reset-defaults-btn">
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset Defaults
          </Button>
          <Button onClick={() => { setEditing({ ...EMPTY_COURIER }); setCreating(true); }} className="bg-[#FF6B00] hover:bg-[#E65A00] rounded-lg" data-testid="add-courier-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Courier
          </Button>
        </div>
      </div>

      {/* Preview tool */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="preview-section">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-[#FF6B00]" />
          <h2 className="font-['Outfit'] font-bold text-slate-900">Live Rate Preview</h2>
          <span className="text-xs text-slate-500">— simulate what customers will see</span>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-xs">Distance (km)</Label>
            <Input type="number" value={previewParams.distance_km} onChange={(e) => setPreviewParams({ ...previewParams, distance_km: parseFloat(e.target.value || 0) })} className="h-10 w-32" data-testid="preview-distance" />
          </div>
          <div>
            <Label className="text-xs">Weight (kg)</Label>
            <Input type="number" step="0.1" value={previewParams.weight_kg} onChange={(e) => setPreviewParams({ ...previewParams, weight_kg: parseFloat(e.target.value || 0) })} className="h-10 w-32" data-testid="preview-weight" />
          </div>
          <Button onClick={runPreview} className="h-10 bg-[#FF6B00] hover:bg-[#E65A00]" data-testid="run-preview-btn">Run Preview</Button>
        </div>
        {preview && (
          <div className="mt-4 grid md:grid-cols-3 gap-2">
            {preview.options.map((o) => (
              <div key={o.id} className="p-3 rounded-lg border border-slate-100 bg-slate-50 flex items-center justify-between" data-testid={`preview-opt-${o.id}`}>
                <div>
                  <p className="text-xs font-bold text-slate-900">{o.name}</p>
                  <p className="text-[10px] text-slate-500">{o.estimated_days}d · ⭐{o.reliability}</p>
                </div>
                <p className="font-['Outfit'] text-lg font-bold text-[#FF6B00]">₹{o.price}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* List */}
      {loading ? <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>
        : <div className="space-y-2">
            {couriers.map((c, i) => (
              <div key={c.id} className={`bg-white rounded-2xl border transition ${c.active ? "border-slate-100" : "border-slate-100 bg-slate-50/60 opacity-70"}`} data-testid={`courier-row-${c.courier_id}`}>
                <div className="p-4 flex items-center gap-3 flex-wrap">
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="p-0.5 hover:bg-slate-100 rounded disabled:opacity-30" data-testid={`move-up-${c.courier_id}`}><ArrowUp className="h-3 w-3" /></button>
                    <button onClick={() => move(i, 1)} disabled={i === couriers.length - 1} className="p-0.5 hover:bg-slate-100 rounded disabled:opacity-30" data-testid={`move-down-${c.courier_id}`}><ArrowDown className="h-3 w-3" /></button>
                  </div>
                  {c.logo ? <img src={c.logo} alt={c.name} className="h-10 w-10 rounded-lg object-contain bg-white border border-slate-100" onError={(e) => { e.target.style.display = "none"; }} />
                    : <div className="h-10 w-10 rounded-lg bg-[#FF6B00]/10 flex items-center justify-center"><Truck className="h-5 w-5 text-[#FF6B00]" /></div>}
                  <div className="flex-1 min-w-[180px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-['Outfit'] font-bold text-slate-900">{c.name}</span>
                      <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{c.courier_id}</span>
                      {!c.active && <span className="text-[10px] font-bold uppercase text-slate-500 bg-slate-200 px-1.5 py-0.5 rounded">OFF</span>}
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-1">{c.tagline}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-600">
                    <span className="flex items-center gap-1"><Package className="h-3 w-3" /> {c.slabs?.length || 0} slabs</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {c.days}d</span>
                    <span className="flex items-center gap-1"><Star className="h-3 w-3 text-amber-500" /> {c.reliability}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => toggleActive(c)} className="rounded-lg h-8 px-2" data-testid={`toggle-${c.courier_id}`}>
                      {c.active ? <ToggleRight className="h-4 w-4 text-emerald-600" /> : <ToggleLeft className="h-4 w-4 text-slate-400" />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditing({ ...c }); setCreating(false); }} className="rounded-lg h-8 px-2" data-testid={`edit-${c.courier_id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(c)} className="rounded-lg h-8 px-2 border-red-200 text-red-600" data-testid={`delete-${c.courier_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>}

      {/* Edit / Create modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => !saving && setEditing(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-modal">
            <div className="sticky top-0 bg-white border-b border-slate-100 p-5 flex items-center justify-between">
              <h3 className="font-['Outfit'] text-lg font-bold text-slate-900">{creating ? "Add Courier" : `Edit ${editing.name}`}</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditing(null)} data-testid="modal-close"><X className="h-4 w-4" /></Button>
            </div>
            <div className="p-5 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1" data-testid="edit-name" />
                </div>
                <div>
                  <Label>ID (slug)</Label>
                  <Input value={editing.courier_id} onChange={(e) => setEditing({ ...editing, courier_id: e.target.value.toLowerCase().replace(/\s+/g, "_") })} disabled={!creating} className="mt-1 font-mono text-sm" data-testid="edit-courier-id" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Logo URL (optional)</Label>
                  <Input value={editing.logo || ""} onChange={(e) => setEditing({ ...editing, logo: e.target.value })} placeholder="https://logo.clearbit.com/example.com" className="mt-1" data-testid="edit-logo" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Tagline</Label>
                  <Input value={editing.tagline || ""} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} placeholder="Short description shown to customer" className="mt-1" data-testid="edit-tagline" />
                </div>
                <div>
                  <Label>ETA (days)</Label>
                  <Input type="number" value={editing.days} onChange={(e) => setEditing({ ...editing, days: e.target.value })} className="mt-1" data-testid="edit-days" />
                </div>
                <div>
                  <Label>Reliability (0-5)</Label>
                  <Input type="number" step="0.1" min="0" max="5" value={editing.reliability} onChange={(e) => setEditing({ ...editing, reliability: e.target.value })} className="mt-1" data-testid="edit-reliability" />
                </div>
                <div>
                  <Label>Per-km extra (₹) — added on top of slab price</Label>
                  <Input type="number" step="0.01" value={editing.per_km_extra} onChange={(e) => setEditing({ ...editing, per_km_extra: e.target.value })} className="mt-1" data-testid="edit-per-km" />
                </div>
                <div>
                  <Label>Priority / sort order</Label>
                  <Input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} className="mt-1" data-testid="edit-sort" />
                </div>
              </div>

              {/* Slabs */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Weight Slabs (₹ price per slab)</Label>
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...editing, slabs: [...(editing.slabs || []), { min_kg: 0, max_kg: 1, price: 100 }] })} className="rounded-lg h-7 px-2 text-xs" data-testid="add-slab-btn">
                    <Plus className="h-3 w-3 mr-1" /> Add Slab
                  </Button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {(editing.slabs || []).map((s, i) => (
                    <div key={`slab-${s._cid || `${s.min_kg}-${s.max_kg}-${i}`}`} className="grid grid-cols-[1fr_1fr_1fr_40px] gap-2 items-end bg-slate-50 rounded-lg p-2" data-testid={`slab-row-${i}`}>
                      <div>
                        <Label className="text-[10px]">Min kg</Label>
                        <Input type="number" step="0.1" value={s.min_kg} onChange={(e) => { const slabs = [...editing.slabs]; slabs[i] = { ...slabs[i], min_kg: e.target.value }; setEditing({ ...editing, slabs }); }} className="h-9" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Max kg</Label>
                        <Input type="number" step="0.1" value={s.max_kg} onChange={(e) => { const slabs = [...editing.slabs]; slabs[i] = { ...slabs[i], max_kg: e.target.value }; setEditing({ ...editing, slabs }); }} className="h-9" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Price ₹</Label>
                        <Input type="number" step="1" value={s.price} onChange={(e) => { const slabs = [...editing.slabs]; slabs[i] = { ...slabs[i], price: e.target.value }; setEditing({ ...editing, slabs }); }} className="h-9" data-testid={`slab-price-${i}`} />
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setEditing({ ...editing, slabs: editing.slabs.filter((_, j) => j !== i) })} className="h-9 text-red-600" data-testid={`delete-slab-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                  {!(editing.slabs || []).length && <p className="text-xs text-slate-400 text-center py-4">No slabs yet — add at least one</p>}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="active-toggle">
                  <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="h-4 w-4" />
                  <span className="text-sm font-semibold">Active (visible to customers)</span>
                </label>
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="flex-1 rounded-xl h-11">Cancel</Button>
                <Button onClick={save} disabled={saving} className="flex-1 bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-11" data-testid="modal-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> {creating ? "Create" : "Save Changes"}</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
